import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";

import { CircularProgress, Typography, Box } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import Modal from "Components/Generic/Modal";


import { NotificationToast } from "Components/Generic/Toast/NotificationToast";

import { Task } from "Models/task";
import { useTranslation } from "react-i18next";
import TaskService from "Services/task.service";
import { getMuiPopupContainer } from "Themes/dispatchTheme";

import { useDespatchActions } from "../../modules/moving/components/Diary/views/DiaryResourcesView/hooks/useDespatchActions";
import { ExpenseManagement, ExpenseManagementRefHandle } from "./components/ExpenseManagement";

import { TimeControlForm } from "./components/TimeControlForm";
import useStyles, { Divider, Wrapper } from "./styles";

import { OrderContext } from "Context/OrderContext";

interface TimeControlAndExpenseProps {}

export interface TimeControlAndExpensePropsHandles {
  open: (taskId?: Task["id"] | Task, isEnabledToEdit?: boolean) => void;
  close: () => void;
}

const TimeControlAndExpenseModalComponent: ForwardRefRenderFunction<
  TimeControlAndExpensePropsHandles,
  TimeControlAndExpenseProps
> = (props, ref) => {
  const classes = useStyles({ width: "70vw" });
  const { t: translate } = useTranslation();

  const { setOrder } = useContext(OrderContext);

  const [taskId, setTaskId] = useState<Task["id"] | null>(null);
  const [task, setTask] = useState<Task | null>(null);
  const { getMembersList, getFleetList } = useDespatchActions();
  const [isOpen, setIsOpen] = useState(false);
  const [isUserEnableToEdit, setIsUserEnableToEdit] = useState(true);
  const expenseManagementRef = useRef<ExpenseManagementRefHandle>(null);

  const onClose = () => {
    setTask(null);
    setTaskId(null);
    setIsOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: (taskParam, isEnabledToEdit = true) => {
      if (typeof taskParam === "number") setTaskId(taskParam);
      if (typeof taskParam === "object") setTask(taskParam);
      setIsOpen(true);
      setIsUserEnableToEdit(isEnabledToEdit);
    },
    close: onClose,
  }));

  const { data: memberList } = getMembersList();
  const { data: fleetList } = getFleetList();

  const { isFetching: isLoadingTask } = useQuery(
    ["tasks.get", taskId],
    () => {
      if (!taskId) return null;

      return TaskService.getById(taskId, {
        include: JSON.stringify([
          {
            association: "members",
          },
          {
            association: "fleets",
          },
        ]),
      }).then(({ data }) => data);
    },
    {
      enabled: !!taskId,
      onSuccess(data) {
        setTask(data);
      },
    }
  );

  const { mutate: onSave, isLoading } = useMutation({
    mutationKey: ["TaskTimeControl", String(taskId)],
    mutationFn: (taskToSave: Task) => TaskService.updateTaskTimeControl(taskToSave.id!, taskToSave),
    onSuccess: (_, taskPayload) => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      setTask(taskPayload);
      if (expenseManagementRef.current) {
        expenseManagementRef.current.refresh();
      }
    },
    onError: () => {
      console.log("Error saving task time control");
      NotificationToast.error(translate("anErrorOccurredTryAgain"));
    },
  });

  const taskMembers = useMemo(() => {
    if (memberList.length) return memberList;

    const taskMembersList = task?.members || [];

    if (task?.memberLeader) taskMembersList.push(task?.memberLeader);

    return taskMembersList;
  }, [task, memberList]);

  const taskFleets = useMemo(() => {
    if (fleetList.length) return fleetList;

    return task?.fleets || [];
  }, [fleetList]);

  const handleTimeControlSave = () => {
    if (task && isUserEnableToEdit) onSave(task);
  };

  const LoadingState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding={4}
      minHeight="200px"
    >
      <CircularProgress size={50} color="primary" />
      <Typography variant="body1" style={{ marginTop: 16 }}>
        {translate("loadingTaskInformation")}
      </Typography>
    </Box>
  );

  const EmptyTaskState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding={4}
      minHeight="200px"
    >
      <Typography variant="h6" color="textSecondary">
        {translate("taskNotFound")}
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
        {translate("couldNotLoadRequestedTaskDetails")}
      </Typography>
    </Box>
  );

  const renderContent = () => {
    if (isLoadingTask) return <LoadingState />;

    if (!task) return <EmptyTaskState />;

    return (
      <>
        <TimeControlForm
          task={task}
          setTask={setTask}
          isUserEnableToEdit={isUserEnableToEdit}
          onSave={handleTimeControlSave}
          isLoading={isLoading}
          refetchExpenses={() => expenseManagementRef.current?.refresh()}
        />

        <Divider />

        <ExpenseManagement
          ref={expenseManagementRef}
          taskId={task?.id!}
          taskStart={task?.startDate}
          taskEnd={task?.endDate}
          fleets={taskFleets}
          members={taskMembers}
          isUserEnableToEdit={isUserEnableToEdit}
        />
      </>
    );
  };

  return (
    <Modal
      open={isOpen}
      title={task?.type}
      handleClose={onClose}
      className={classes.mainDialog}
      showModalFooter={false}
      container={getMuiPopupContainer}
    >
      <Wrapper>{renderContent()}</Wrapper>
    </Modal>
  );
};

export const TimeControlAndExpenseModal = forwardRef(TimeControlAndExpenseModalComponent);
